import { useState } from 'react';

export default function Services() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const services = [
    {
      id: 'website-design',
      icon: 'ri-macbook-line',
      title: 'Website Design',
      description: 'Custom websites that help Long Island small businesses stand out online and convert visitors into customers.',
      features: ['Mobile-Responsive', 'SEO Optimized', 'Fast Loading']
    },
    {
      id: 'ethical-hacking',
      icon: 'ri-shield-keyhole-line',
      title: 'Ethical Hacking',
      description: 'Protect your business from cyber threats with comprehensive security testing and vulnerability assessments.',
      features: ['Penetration Testing', 'Security Audits', 'Risk Assessment']
    },
    {
      id: 'social-media',
      icon: 'ri-chat-smile-2-line',
      title: 'Social Media',
      description: 'Build your local presence and engage customers across Nassau and Suffolk County with strategic content.',
      features: ['Content Strategy', 'Community Growth', 'Local Targeting']
    },
    {
      id: 'videography',
      icon: 'ri-video-line',
      title: 'Videography',
      description: 'Professional video production that showcases your Long Island business and tells your brand story.',
      features: ['Commercial Shoots', 'Brand Films', 'Promotional Videos']
    },
    {
      id: 'video-editing',
      icon: 'ri-movie-2-line',
      title: 'Video Editing',
      description: 'Expert post-production that transforms your footage into polished, professional content.',
      features: ['Color Grading', 'Motion Graphics', 'Sound Design']
    }
  ];

  const scrollToProject = () => {
    const element = document.getElementById('start-project');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-32 bg-neutral-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-20">
          <p className="text-sm font-medium text-[#1E3A8A] tracking-wide mb-4">
            OUR SERVICES
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight mb-6">
            Everything Your Business Needs
          </h2>
          <p className="text-lg text-neutral-500 max-w-2xl mx-auto">
            From stunning websites to engaging videos, we help small businesses in Nassau and Suffolk County reach more customers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 3).map((service) => (
            <div
              key={service.id}
              onMouseEnter={() => setHoveredId(service.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={scrollToProject}
              className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-neutral-100 hover:border-[#1E3A8A]/20"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6 group-hover:bg-[#1E3A8A] transition-all duration-300">
                <i className={`${service.icon} text-2xl text-[#1E3A8A] group-hover:text-white transition-colors duration-300`}></i>
              </div>
              
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                {service.title}
              </h3>
              
              <p className="text-sm text-neutral-500 leading-relaxed mb-6">
                {service.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature, idx) => (
                  <span 
                    key={idx}
                    className="text-xs font-medium text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 max-w-4xl mx-auto">
          {services.slice(3).map((service) => (
            <div
              key={service.id}
              onMouseEnter={() => setHoveredId(service.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={scrollToProject}
              className="bg-white rounded-2xl p-8 transition-all duration-500 cursor-pointer group hover:shadow-xl hover:shadow-neutral-200/50"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-all duration-500 ${
                hoveredId === service.id ? 'bg-[#1E3A8A]' : 'bg-neutral-100'
              }`}>
                <i className={`${service.icon} text-2xl transition-colors duration-500 ${
                  hoveredId === service.id ? 'text-white' : 'text-neutral-600'
                }`}></i>
              </div>
              
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                {service.title}
              </h3>
              
              <p className="text-sm text-neutral-500 leading-relaxed mb-6">
                {service.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature, idx) => (
                  <span 
                    key={idx}
                    className="text-xs font-medium text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-neutral-500 mb-4">Ready to grow your business?</p>
          <a 
            href="tel:+16316407888"
            className="inline-flex items-center gap-2 text-lg font-semibold text-[#1E3A8A] hover:text-[#1E40AF] transition-colors cursor-pointer"
          >
            <i className="ri-phone-fill"></i>
            Call (631) 640-7888
          </a>
        </div>
      </div>
    </section>
  );
}
